/**
 * <tf-topbar> — Topbar canonica condivisa tra tutte le pagine app.
 *
 * Sostituisce gli header duplicati con drift già rilevato (3 hash diversi
 * in dashboard.html / clienti.html / report.html).
 *
 * Render:
 *   - Search pill (Cmd+K opener — usa window.TFGlobalSearch)
 *   - Title (configurable via attribute o data-page)
 *   - Notif bell mount point (#notif-mount, popolato da notif-bell.js)
 *   - Logout button (chiama window.AP.logout() o window.tfAuth.clearAuth())
 *
 * Uso:
 *   <tf-topbar title="Dashboard"></tf-topbar>
 *   <tf-topbar title="Fatture" cta-href="/nuova-fattura.html" cta-label="Nuova fattura"></tf-topbar>
 *
 * Attributes:
 *   - title: string (default: detect da document.title o data-page)
 *   - cta-href: optional CTA link
 *   - cta-label: optional CTA label
 *   - hide-search: "true" per nascondere search pill
 *   - hide-logout: "true" per nascondere logout
 *
 * Slots:
 *   - default: contenuto custom inserito tra title e actions (es. tabs/filters)
 *   - actions: contenuto extra prima del logout button
 *
 * @since v9.16 (Sprint 3)
 */

export class TfTopbarElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['title', 'cta-href', 'cta-label', 'hide-search', 'hide-logout'];
  }

  connectedCallback(): void {
    this._render();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this._render();
  }

  private _detectTitle(): string {
    const attrTitle = this.getAttribute('title');
    if (attrTitle) return attrTitle;
    // Fallback: detect da document.title (es. "Dashboard | TurboFattura" → "Dashboard")
    const docTitle = document.title || '';
    const cleaned = docTitle.split('|')[0].split('—')[0].trim();
    return cleaned || 'TurboFattura';
  }

  private _render(): void {
    const title = this._detectTitle();
    const ctaHref = this.getAttribute('cta-href');
    const ctaLabel = this.getAttribute('cta-label') || 'Crea';
    const showSearch = this.getAttribute('hide-search') !== 'true';
    const showLogout = this.getAttribute('hide-logout') !== 'true';

    // Salva slot custom (children iniziali, prima del primo render)
    const slottedActions = this.querySelector('[slot="actions"]')?.outerHTML || '';
    const defaultSlot = Array.from(this.childNodes)
      .filter(
        (n) =>
          !(n instanceof Element && (n.getAttribute('slot') === 'actions' || n.tagName.toLowerCase().startsWith('header')))
      )
      .map((n) => (n instanceof Element ? n.outerHTML : (n.textContent ?? '')))
      .join('');

    this.innerHTML = `
      <header class="sticky top-0 z-30 flex h-16 items-center justify-between bg-background-1/80 px-3 sm:px-6 backdrop-blur-xl">
        <div class="flex items-center gap-4">
          ${
            showSearch
              ? `<button type="button" onclick="window.TFGlobalSearch && TFGlobalSearch.open()" id="tf-cmdk-pill" aria-label="Apri ricerca globale" title="Cerca (⌘K)" class="tf-cmdk-pill">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
                  </svg>
                  <span class="tf-cmdk-pill-label">Cerca</span>
                  <kbd class="tf-cmdk-pill-kbd">⌘K</kbd>
                </button>`
              : ''
          }
          <h1 class="text-lg font-semibold text-secondary">${this._escapeHtml(title)}</h1>
          ${defaultSlot}
        </div>
        <div class="flex items-center gap-3">
          ${slottedActions}
          ${
            ctaHref
              ? `<a href="${this._escapeHtml(ctaHref)}" class="hidden sm:inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary-600 transition-colors">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
                  ${this._escapeHtml(ctaLabel)}
                </a>`
              : ''
          }
          <div data-notif-mount></div>
          ${
            showLogout
              ? `<button type="button" data-tf-logout class="rounded-lg p-2 text-secondary/40 transition-all hover:bg-red-50 hover:text-red-500" title="Esci" aria-label="Esci">
                  <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15"/>
                    <path d="m18 15 3-3m0 0-3-3m3 3H9"/>
                  </svg>
                </button>`
              : ''
          }
        </div>
      </header>
    `;

    // Wire logout button
    const logoutBtn = this.querySelector<HTMLButtonElement>('[data-tf-logout]');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        // Prefer global AP.logout() (legacy), fallback a tfAuth.clearAuth()
        const w = window as Window & {
          AP?: { logout?: () => void };
          tfAuth?: { clearAuth?: () => void };
        };
        if (w.AP && typeof w.AP.logout === 'function') {
          w.AP.logout();
        } else if (w.tfAuth && typeof w.tfAuth.clearAuth === 'function') {
          w.tfAuth.clearAuth();
          window.location.href = '/login';
        } else {
          // Fallback hard
          localStorage.removeItem('turbofattura_token');
          localStorage.removeItem('turbofattura_user');
          window.location.href = '/login';
        }
      });
    }
  }

  private _escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => {
      switch (c) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#39;';
        default: return c;
      }
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tf-topbar')) {
  customElements.define('tf-topbar', TfTopbarElement);
}
